import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import {
  mergeClasses,
  webLightTheme,
  FluentProvider,
  AriaLiveAnnouncer,
} from '@fluentui/react-components';
import { TextareaValueData } from '@fluentai/textarea';
import { CopilotProvider } from '@fluentai/react-copilot';
import { I18nProvider, DEFAULT_LOCALE, useI18nContext } from '@bingads-webui/copilot-i18n';
import { CopilotResizer, getCopilotWidthFromPreference } from '@bingads-webui-react/copilot-resizer';
import {
  useStructuredLog,
  dispatchStructuredLogMessageEvent,
  dispatchStructuredLogUserEngagementEvent,
  validateStructuredLog,
} from '@bingads-webui-react/copilot-logger';
import { type BotActivity } from '@bingads-webui/ads-copilot-shared-types';
import { ChatHistoryView, EndLiveAgentDialog, useChatHistoryPerfMarkers, ChatHistoryTeachingBubble } from '@bingads-webui-react/copilot-chat-history';
import { Footer } from '@bingads-webui-react/copilot-ui-base';
import { getPrivacyStatementLink } from './utils';
import { ConversationId } from './ConversationId';
import { IDirectLineError } from './botTypes';
import { type IAdsCopilotProps, type IConversationThreadProps } from './types';
import { AutoScroller } from './AutoScroller';
import { ConversationThread, DEFAULT_GROUP_TIME } from './components/conversation-thread';
import { DirectLineProvider } from './components/direct-line-provider';
import { LoadingProvider } from './components/loading-provider';
import { useAdsCopilotContext } from './components/ads-copilot-context-provider';
import { useStaticMessage } from './hooks/useStaticMessage';
import { useSesId } from './hooks/useSesId';
import { useAutoReloadGuard } from './hooks/useAutoReloadGuard';
import { useChatSuggestions } from './hooks/useChatSuggestions';
import { useInputDisabled } from './hooks/useInputDisabled';
import { useAdsCopilotStyles } from './hooks/useAdsCopilotStyles';
import { usePostMessage } from './hooks/use-post-message';
import { ChatInput } from './ChatInput';
import { DirectLineErrorView } from './ErrorView';
import { PromptBar } from './PromptBar';
import { Header } from './components/header/header';

const COPILOT_WIDTH = 320;

const AdsCopilotInternal: FC<IAdsCopilotProps> = memo((props) => {
  const {
    onClose,
    botServiceConfig,
    onConnectionStatusChange,
    groupTime,
    renderAction,
    renderActivityFooter,
    onSendActivityError,
    onFeedbackButtonClick,
    renderHeaderComponent,
    renderFooterComponent,
    renderTopComponent,
    renderBottomComponent,
    onReferenceClick,
    onHeadsetClick,
    onSupportClick,
    showPreviewTag,
    showHeadsetButton,
    showSupportButton,
    maxInputLength,
    characterCountRevealThreshold,
    headerContent,
    title,
    customStream,
    isTokenExpiredAutoReloadEnabled,
    onAutoReloading,
    isStopGeneratingEnabled,
    showViewPrompt,
    onViewPromptClick,
    isChatSuggestionsEnabled,
    wrapSuggestionText,
    maxSuggestions,
    contactSupportText,
    onContactSupportClick,
    isChatHistoryEnabled,
    isTroubleshootingEnabled,
    isReportingInsightsEnabled,
    isUxImprovementsEnabled: isUxImprovementsEnabledProp,
    onChatSuggestionClick,
    renderCustomErrorView,
    onChangeChatInput,
    isCopilotDegraded,
    isShowFeaturesInHint,
    onAIDegrade,
    logger,
    onReceiveActivityEnd,
    showHeader,
    isConnectedToLiveAgent,
    shouldDisableInput,
    locale,
    isMessagePopoverEnabled,
    isEntitySelectionMenuEnabled,
    entitySelectionPerfMarker,
    sessionManager,
    appPerfMarker,
    selectedPromptItem,
    resetSelectedPromptItem,
    isPROD,
    conversationHasMessages,
    isLiveAgentInProgress,
    onEndLiveAgentChatClick,
    getAIMessageMarkdownOverrides,
    getTailorContext,
    isNavRefreshNew,
  } = props;
  const autoScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const resetConversation = React.useRef<boolean>(false);
  const [conversationId, setConversationId] = useState<string>('');
  const [activityId, setActivityId] = useState<string>('');
  const [isEndLiveAgentDialogOpen, setIsEndLiveAgentDialogOpen] = useState(false);
  const i18n = useI18nContext();

  const shouldSkipStructuredLogValidation = botServiceConfig.productName === 'msads_landing_page' || botServiceConfig.productName === 'ads_marketing';

  /* Registers event listeners for structured log */
  const { isReady: isStructuredLogReady } = useStructuredLog({
    botServiceConfig,
    isCustomStream: !!customStream,
    locale: locale as string,
    conversationId,
    structuredLogValidator: shouldSkipStructuredLogValidation ? null : validateStructuredLog,
    logger: {
      log: data => logger?.trace(`[Structured log] ${JSON.stringify(data)}`),
      error: (data, errors) => logger?.trace(`[Structured log] ${JSON.stringify({ ...data, errors })}`),
    },
  });

  const styles = useAdsCopilotStyles();
  const { resumePerfMarker } = useChatHistoryPerfMarkers(appPerfMarker);
  const {
    sesId, onClickStartOver, onInitDirectLine, directLine,
  } = useSesId(props);
  const { canAutoReload, recordAutoReload } = useAutoReloadGuard(logger);
  const [chatInputValue, setChatInputValue] = useState('');

  const [showChatHistory, setShowChatHistory] = useState(false);
  const existingToken = useRef<string | null>(null);

  const handleChatHistoryClick = useCallback(() => {
    if (isConnectedToLiveAgent) {
      setIsEndLiveAgentDialogOpen(true);
    } else {
      logger?.trace('[Chat History] Clicked chat history icon');
      setShowChatHistory(true);
    }
  }, [isConnectedToLiveAgent, logger]);

  const handleEndLiveAgentConfirm = useCallback(() => {
    setIsEndLiveAgentDialogOpen(false);
    setShowChatHistory(true);
  }, []);

  const handleChatHistoryConversationClick = useCallback(async (conversationToken: string) => {
    await onClickStartOver();
    existingToken.current = conversationToken;
    setShowChatHistory(false);
    resumePerfMarker?.done();
    dispatchStructuredLogMessageEvent({
      activity: {
        id: activityId,
        conversation: { id: conversationId },
        type: 'message',
        value: 'chatHistoryResume',
      } as any,
    });
  }, [existingToken, onClickStartOver, resumePerfMarker, activityId, conversationId]);

  const onClickStartOverWithResetConversation = useCallback(() => {
    resetConversation.current = true;
    onClickStartOver();
  }, [onClickStartOver]);

  const handleChatHistoryBack = useCallback(() => {
    onClickStartOver();
    setShowChatHistory(false);
  }, [onClickStartOver]);

  const onDeleteActiveConversation = useCallback(async () => {
    onClickStartOverWithResetConversation();
    sessionManager?.saveSessionInfo({ isOpen: true, token: '' });
  }, [sessionManager, onClickStartOverWithResetConversation]);

  const handleStartNewChat = useCallback(() => {
    onClickStartOverWithResetConversation();
    setShowChatHistory(false);
  }, [onClickStartOverWithResetConversation]);

  const {
    renderActivity,
  } = useStaticMessage({
    ...props,
    onClickStartOver: onClickStartOverWithResetConversation,
    logger,
  });

  const {
    sendDisabled,
    inputDisabled,
    onSendActivity,
    onReceiveActivity,
  } = useInputDisabled({
    ...props,
    botServiceConfig,
  });

  const {
    postMessage,
  } = usePostMessage({
    ...props,
    directLine,
    isCustomStream: !!customStream,
    botServiceConfig,
    onSendActivity,
    onSendActivityError,
    getTailorContext,
  });

  const onChangeChatInputInternal = useCallback((_ev: any, { value: v }: TextareaValueData) => {
    setChatInputValue(v);
    onChangeChatInput?.();
  }, [onChangeChatInput]);

  const handleChatInputPostMessage = useCallback((_evt: any, { value }: TextareaValueData) => {
    if (postMessage(_evt, { value })) {
      setChatInputValue('');
    }
  }, [postMessage]);

  const handleConversationThreadSuggestionClick = useCallback<Exclude<IConversationThreadProps['onSuggestionClick'], undefined>>((_evt, value, messageOptions) => {
    if (postMessage(_evt, { value }, messageOptions)) {
      setChatInputValue('');
    }
  }, [postMessage]);

  const handleChatSuggestionClick = useCallback((value: string) => {
    onChatSuggestionClick?.(value);
    if (postMessage(null, { value })) {
      setChatInputValue('');
    }
  }, [postMessage, onChatSuggestionClick]);

  const handleTermsOfUseClick = useCallback(() => {
    const link = getPrivacyStatementLink(i18n);
    window.open(link, '_blank');
  }, [i18n]);

  const {
    onReceiveActivity: onReceiveActivityWithChatSuggestions,
    renderChatSuggestionList,
  } = useChatSuggestions({
    onReceiveActivity,
    handleChatSuggestionClick,
    isChatSuggestionsEnabled,
    wrapSuggestionText,
    maxSuggestions,
    contactSupportText,
    onContactSupportClick,
  });

  const handleAutoReloading = useCallback(() => {
    recordAutoReload();
    onAutoReloading?.();
  }, [recordAutoReload, onAutoReloading]);

  const renderDirectLineErrorView = useCallback(
    (error: IDirectLineError) => (
      <DirectLineErrorView
        error={error}
        onRefresh={onClickStartOverWithResetConversation}
        isTokenExpiredAutoReloadEnabled={isTokenExpiredAutoReloadEnabled && canAutoReload()}
        onAutoReloading={handleAutoReloading}
      />
    ),
    [onClickStartOverWithResetConversation, isTokenExpiredAutoReloadEnabled, canAutoReload, handleAutoReloading]
  );

  const onReceiveActivityStart = useCallback((
    activity: BotActivity,
    finalActivities: BotActivity[]
  ) => {
    onAIDegrade?.(activity, finalActivities, postMessage);

    /* Store conversationId and activityId for structured log from activity */
    const newConversationId = activity.conversation?.id || '';
    const newActivityId = activity.id || '';

    if (!customStream && conversationId !== newConversationId) {
      setConversationId(newConversationId);
    }

    if (activityId !== newActivityId) {
      setActivityId(newActivityId);
    }

    dispatchStructuredLogMessageEvent({ activity });
    dispatchStructuredLogUserEngagementEvent({ activity });
  }, [onAIDegrade, postMessage, customStream, conversationId, activityId, botServiceConfig, locale]);

  const adsCopilotContext = useAdsCopilotContext();

  useEffect(() => {
    adsCopilotContext?.onBotServiceConfigChange?.(botServiceConfig);
  }, [adsCopilotContext?.onBotServiceConfigChange, botServiceConfig]);

  const isUxImprovementsEnabled = isUxImprovementsEnabledProp || adsCopilotContext?.pilotMap?.isUxImprovementsEnabled;
  const isMarketingIntegrationEnabled = adsCopilotContext?.pilotMap?.isMarketingIntegrationEnabled;
  const isRefreshUIEnabled = adsCopilotContext?.pilotMap?.isRefreshUIEnabled;
  const isProgressIndicatorEnabled = adsCopilotContext?.pilotMap?.isProgressIndicatorEnabled;

  useEffect(() => {
    if (adsCopilotContext?.chatTriggerSource && isStructuredLogReady && conversationId) {
      dispatchStructuredLogUserEngagementEvent({
        activity: {
          id: activityId,
          conversation: { id: conversationId },
        } as any,
        options: {
          chatTriggerSource: {
            source: adsCopilotContext.chatTriggerSource,
          },
        },
      });
    }
  }, [adsCopilotContext?.chatTriggerSource, isStructuredLogReady, conversationId]);

  const isLargePane = useMemo(() => {
    if (props.resizerProps?.preferenceService) {
      const width = getCopilotWidthFromPreference(true, props.resizerProps?.preferenceService);
      return width > 420;
    }
    return false;
  }, [props.resizerProps?.preferenceService]);


  return (
    <DirectLineProvider
      {...botServiceConfig}
      sesId={sesId}
      renderErrorView={renderDirectLineErrorView}
      renderCustomErrorView={renderCustomErrorView}
      onInitDirectLine={onInitDirectLine}
      onConnectionStatusChange={onConnectionStatusChange}
      customStream={customStream}
      resetConversation={resetConversation}
      existingToken={existingToken}
      logger={logger}
      isMarketingIntegrationEnabled={isMarketingIntegrationEnabled}
    >
      <LoadingProvider
        onReceiveActivity={onReceiveActivityWithChatSuggestions}
        renderBottomComponent={renderBottomComponent}
        isCustomStream={!!customStream}
        isStopGeneratingEnabled={isStopGeneratingEnabled}
        isStopTroubleshootingEnabled={isTroubleshootingEnabled}
        isRefreshUIEnabled={isRefreshUIEnabled}
        isProgressIndicatorEnabled={isProgressIndicatorEnabled}
        onEndLiveAgentChatClick={onEndLiveAgentChatClick}
        isLiveAgentInProgress={isLiveAgentInProgress}

      >
        {(!isChatHistoryEnabled || !showChatHistory) && (
        <div className={mergeClasses(styles.root, isNavRefreshNew && styles.navRefresh, 'ads-copilot-root')} >
          {showHeader && (
          <Header
            showPreviewTag={showPreviewTag}
            showHeadsetButton={showHeadsetButton}
            showSupportButton={showSupportButton}
            onHeadsetClick={onHeadsetClick}
            onSupportClick={onSupportClick}
            onRefresh={onClickStartOverWithResetConversation}
            onClose={onClose}
            title={title}
            onChatHistoryClick={handleChatHistoryClick}
            isChatHistoryEnabled={isChatHistoryEnabled}
            isUxImprovementsEnabled={isUxImprovementsEnabled}
            isRefreshUIEnabled={isRefreshUIEnabled}
            logger={logger}
            onTermsOfUseClick={handleTermsOfUseClick}
            isNavRefreshNew={isNavRefreshNew}
          >
            {headerContent}
          </Header>
        )}
          {renderHeaderComponent?.()}
          <ConversationId conversationId={conversationId} />
          <div className={mergeClasses(styles.container, isNavRefreshNew && styles.containerNavRefresh, 'chat-container')}>
            <AutoScroller
              className="conversation-history-container"
              autoScrollContainerRef={autoScrollContainerRef}
              isStartPointOfNewConversation={sendDisabled}
              shouldHideScroll={!conversationHasMessages}
              isCustomStream={!!customStream}
            >
              <ConversationThread
                ref={autoScrollContainerRef}
                onReceiveActivityEnd={onReceiveActivityEnd}
                onReceiveActivityStart={onReceiveActivityStart}
                renderActivity={renderActivity}
                groupTime={groupTime}
                renderTopComponent={renderTopComponent}
                renderAction={renderAction}
                renderActivityFooter={renderActivityFooter}
                onFeedbackButtonClick={onFeedbackButtonClick}
                onReferenceClick={onReferenceClick}
                isCustomStream={!!customStream}
                isStopGeneratingEnabled={isStopGeneratingEnabled}
                isTroubleshootingEnabled={isTroubleshootingEnabled}
                isReportingInsightsEnabled={isReportingInsightsEnabled}
                onSuggestionClick={handleConversationThreadSuggestionClick}
                renderChatSuggestionList={renderChatSuggestionList}
                isCopilotDegraded={isCopilotDegraded}
                isShowFeaturesInHint={isShowFeaturesInHint}
                isMessagePopoverEnabled={isMessagePopoverEnabled}
                getAIMessageMarkdownOverrides={getAIMessageMarkdownOverrides}
              />
            </AutoScroller>
            {renderFooterComponent?.()}
            {showViewPrompt && <PromptBar onViewPromptClick={onViewPromptClick} />}
            <ChatInput
              inputDisabled={inputDisabled || shouldDisableInput}
              sendDisabled={sendDisabled}
              maxLength={maxInputLength}
              characterCountRevealThreshold={characterCountRevealThreshold}
              onChange={onChangeChatInputInternal}
              postMessage={handleChatInputPostMessage}
              value={chatInputValue}
              botServiceConfig={botServiceConfig}
              logger={logger}
              isEntitySelectionMenuEnabled={isEntitySelectionMenuEnabled}
              entitySelectionPerfMarker={entitySelectionPerfMarker}
              selectedPromptItem={selectedPromptItem}
              resetSelectedPromptItem={resetSelectedPromptItem}
            />
            {isRefreshUIEnabled && <Footer conversationHasMessages={conversationHasMessages} disclaimerText={isMarketingIntegrationEnabled ? _TL_('AI-generated content may be incorrect') : undefined} /> }
          </div>
        </div>
      )}
        {showChatHistory && isChatHistoryEnabled && (
        <ChatHistoryView
          cid={botServiceConfig.customerId}
          uid={botServiceConfig.userid}
          onConversationClick={handleChatHistoryConversationClick}
          onClose={onClose}
          onBack={handleChatHistoryBack}
          logger={logger}
          activeConversationId={conversationId}
          onDeleteActiveConversation={onDeleteActiveConversation}
          onStartNewChat={handleStartNewChat}
          appPerfMarker={appPerfMarker}
          isRefreshUIEnabled={isRefreshUIEnabled}
          isNavRefreshNew={isNavRefreshNew}
        />
      )}
        {isChatHistoryEnabled && (
        <>
          <EndLiveAgentDialog
            open={isEndLiveAgentDialogOpen}
            setOpen={setIsEndLiveAgentDialogOpen}
            onConfirm={handleEndLiveAgentConfirm}
          />
          <ChatHistoryTeachingBubble
            isLargePane={isLargePane}
            isPROD={isPROD}
            uid={botServiceConfig.userid}
            cid={botServiceConfig.customerId}
            logger={logger}
          />
        </>
      )}
      </LoadingProvider>
    </DirectLineProvider>
  );
});

const AdsCopilotBase: FC<IAdsCopilotProps> = memo((props) => {
  const {
    bingAdsI18n,
    className,
    currencyCode,
    locale,
    theme,
  } = props;
  const styles = useAdsCopilotStyles();

  return (
    <I18nProvider bingAdsI18n={bingAdsI18n} currencyCode={currencyCode} locale={locale}>
      <FluentProvider theme={theme} className={mergeClasses(styles.provider, className, 'ads-copilot')} applyStylesToPortals={false}>
        <AriaLiveAnnouncer>
          <CopilotProvider
            className={styles.wrapper}
            mode="sidecar"
          >
            <AdsCopilotInternal {...props} />
          </CopilotProvider>
        </AriaLiveAnnouncer>
      </FluentProvider>
    </I18nProvider>
  );
});

export const AdsCopilot: FC<IAdsCopilotProps> = memo((props) => {
  const { enableResizer, resizerProps, isNavRefreshNew } = props;

  if (enableResizer) {
    return (
      <CopilotResizer {...resizerProps} isNavRefreshNew={isNavRefreshNew} >
        <AdsCopilotBase {...props} />
      </CopilotResizer>
    );
  }

  return (
    <div style={{ width: COPILOT_WIDTH, height: '100%' }}>
      <AdsCopilotBase {...props} />
    </div>
  );
});

AdsCopilotBase.defaultProps = {
  onInitDirectLine: undefined,
  onConnectionStatusChange: undefined,
  onReceiveActivity: undefined,
  onClose: undefined,
  renderActivity: undefined,
  onSendActivity: undefined,
  onSendActivityError: undefined,
  renderBottomComponent: undefined,
  renderAction: undefined,
  renderActivityFooter: undefined,
  onFeedbackButtonClick: undefined,
  groupTime: DEFAULT_GROUP_TIME,
  locale: DEFAULT_LOCALE,
  className: '',
  showPreviewTag: true,
  maxInputLength: 500,
  headerContent: undefined,
  onReferenceClick: undefined,
  theme: webLightTheme,
  onStartOverClick: undefined,
  isTokenExpiredAutoReloadEnabled: false,
  onAutoReloading: undefined,
  showViewPrompt: false,
  onViewPromptClick: undefined,
  isChatSuggestionsEnabled: false,
  renderCustomErrorView: undefined,
  isCopilotDegraded: false,
  onAIDegrade: undefined,
  showHeader: true,
  shouldDisableInput: false,
  selectedPromptItem: undefined,
  resetSelectedPromptItem: undefined,
};

AdsCopilot.defaultProps = {
  enableResizer: false,
  resizerProps: {},
};
